import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { routeToHash, type CourseRoute } from '@/lib/courseRoute';

/**
 * CourseLink · a real `<a href="#/...">` for a course page (28 JUN · open-in-new-tab).
 *
 * Plain left-click → `e.preventDefault()` + `onNavigate()` (SPA navigation, no reload).
 * Modified clicks (Ctrl/⌘/Shift/Alt) and non-primary buttons (middle-click) fall
 * through to the browser, which opens the href — i.e. the course page in a new
 * tab/window. The `href` is the same hash the in-app router parses on load, so the
 * new tab lands on the exact page.
 *
 * Drop-in for the old `<button onClick={() => goX(id)}>`: pass the same handler as
 * `onNavigate` and the route. Style via `className` — add `course-link` (globals.css)
 * to neutralise the default anchor underline/color when wrapping card-like content.
 */
interface CourseLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'> {
  route: CourseRoute;
  onNavigate: () => void;
  children: ReactNode;
}

export function CourseLink({ route, onNavigate, children, ...rest }: CourseLinkProps) {
  const href = routeToHash(route);
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented) return;
    // Let the browser open a new tab/window for modified or non-primary clicks.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onNavigate();
  };
  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
